import React, { useState, useEffect } from 'react';
import { Shield, ChevronDown, ChevronUp, CheckCircle2, AlertCircle } from 'lucide-react';
import { roleService } from '../../../services/roleService';

export default function PermissionAssignmentStep({ wizardData, updateWizardData, setError }) {
  const [expandedRoles, setExpandedRoles] = useState({});
  const [selectedRole, setSelectedRole] = useState(wizardData?.roles?.[0]?.tempId || null);

  // Get system features and access levels
  const systemFeatures = roleService?.getSystemFeatures();
  const accessLevels = roleService?.getAccessLevels();

  // Initialize permissions if not set
  React.useEffect(() => {
    if (!wizardData?.permissions || Object.keys(wizardData?.permissions)?.length === 0) {
      const initialPermissions = {};
      wizardData?.roles?.forEach(role => {
        initialPermissions[role.tempId] = {};
      });
      updateWizardData('permissions', initialPermissions);
    }
  }, []);

  // Toggle role expansion
  const toggleRoleExpansion = (roleId) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleId]: !prev?.[roleId]
    }));
  };

  // Update permission for specific role and feature
  const updatePermission = (roleId, feature, accessLevel) => {
    const currentPermissions = { ...wizardData?.permissions };
    if (!currentPermissions?.[roleId]) {
      currentPermissions[roleId] = {};
    }
    currentPermissions[roleId][feature] = accessLevel;
    updateWizardData('permissions', currentPermissions);
  };

  // Apply template permissions to role
  const applyPermissionTemplate = (roleId, template) => {
    const currentPermissions = { ...wizardData?.permissions };
    currentPermissions[roleId] = { ...template };
    updateWizardData('permissions', currentPermissions);
    setError?.('');
  };

  // Permission templates for common roles
  const permissionTemplates = {
    manager: {
      name: 'Manager Template',
      description: 'Full access to core features with admin control',
      permissions: {
        accounts: 'admin',
        contacts: 'admin',
        deals: 'admin',
        customers: 'admin',
        reports: 'admin',
        calendar: 'write',
        activities: 'admin',
        pipeline: 'admin',
        invoices: 'write',
        services: 'write',
        users: 'read',
        settings: 'read'
      }
    },
    sales: {
      name: 'Sales Representative Template',
      description: 'Access to sales and customer-facing features',
      permissions: {
        accounts: 'write',
        contacts: 'write',
        deals: 'write',
        customers: 'write',
        reports: 'read',
        calendar: 'write',
        activities: 'write',
        pipeline: 'write',
        invoices: 'read',
        services: 'read',
        emails: 'write'
      }
    },
    support: {
      name: 'Customer Service Template',
      description: 'Support and customer management focused',
      permissions: {
        contacts: 'write',
        customers: 'write',
        calendar: 'write',
        activities: 'write',
        services: 'read',
        invoices: 'read',
        notifications: 'write'
      }
    }
  };

  // Get access level badge color
  const getAccessLevelBadge = (level) => {
    const badges = {
      none: 'bg-gray-100 text-gray-600',
      read: 'bg-blue-100 text-blue-700',
      write: 'bg-green-100 text-green-700',
      delete: 'bg-orange-100 text-orange-700',
      admin: 'bg-purple-100 text-purple-700'
    };
    return badges?.[level] || badges?.none;
  };

  // Count permissions for a role
  const getPermissionCount = (roleId) => {
    const permissions = wizardData?.permissions?.[roleId] || {};
    return Object.values(permissions)?.filter(level => level !== 'none')?.length;
  };

  if (wizardData?.roles?.length === 0) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No roles available</h3>
        <p className="text-gray-600">Please create roles in Step 1 before assigning permissions</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Assign Permissions</h2>
        <p className="mt-2 text-gray-600">
          Configure access levels for each role across different CRM modules
        </p>
      </div>
      {/* Permission Templates */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Start: Apply Permission Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(permissionTemplates)?.map(([key, template]) => (
            <div
              key={key}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <h4 className="font-semibold text-gray-900 mb-2">{template?.name}</h4>
              <p className="text-sm text-gray-600 mb-3">{template?.description}</p>
              <p className="text-xs text-gray-500 mb-3">
                {Object.values(template?.permissions)?.filter(l => l !== 'none')?.length} permissions configured
              </p>
              <select
                onChange={(e) => {
                  if (e?.target?.value) {
                    applyPermissionTemplate(e?.target?.value, template?.permissions);
                    e.target.value = '';
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                defaultValue=""
              >
                <option value="">Apply to role...</option>
                {wizardData?.roles?.map(role => (
                  <option key={role?.tempId} value={role?.tempId}>
                    {role?.displayName}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
      {/* Permission Matrix */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configure Permissions by Role</h3>
        
        <div className="space-y-4">
          {wizardData?.roles?.map((role) => {
            const isExpanded = expandedRoles?.[role?.tempId];
            const permissionCount = getPermissionCount(role?.tempId);
            const rolePermissions = wizardData?.permissions?.[role?.tempId] || {};

            return (
              <div key={role?.tempId} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Role Header */}
                <div
                  className="bg-gray-50 px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleRoleExpansion(role?.tempId)}
                >
                  <div className="flex items-center space-x-3">
                    <Shield className="w-5 h-5 text-gray-600" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{role?.displayName}</h4>
                      <p className="text-sm text-gray-600">
                        {permissionCount} {permissionCount === 1 ? 'permission' : 'permissions'} assigned
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {permissionCount === 0 && (
                      <span className="flex items-center text-sm text-orange-600">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        No permissions set
                      </span>
                    )}
                    {permissionCount > 0 && (
                      <span className="flex items-center text-sm text-green-600">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Configured
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                {/* Permission Grid */}
                {isExpanded && (
                  <div className="p-6 bg-white">
                    <div className="grid grid-cols-1 gap-4">
                      {systemFeatures?.map((feature) => {
                        const currentLevel = rolePermissions?.[feature?.id] || 'none';
                        
                        return (
                          <div key={feature?.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{feature?.name}</h5>
                              <p className="text-sm text-gray-600">{feature?.description}</p>
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              {accessLevels?.map((level) => (
                                <button
                                  key={level?.id}
                                  onClick={() => updatePermission(role?.tempId, feature?.id, level?.id)}
                                  className={`
                                    px-4 py-2 rounded-lg text-sm font-medium transition-all
                                    ${currentLevel === level?.id
                                      ? `${getAccessLevelBadge(level?.id)} ring-2 ring-offset-1 ring-blue-500`
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }
                                  `}
                                  title={level?.description}
                                >
                                  {level?.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">📊 Permission Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {wizardData?.roles?.map(role => {
            const count = getPermissionCount(role?.tempId);
            return (
              <div key={role?.tempId} className="bg-white rounded-lg p-3">
                <p className="text-gray-600 text-xs mb-1">{role?.displayName}</p>
                <p className="text-lg font-bold text-blue-900">{count}</p>
                <p className="text-gray-500 text-xs">permissions</p>
              </div>
            );
          })}
        </div>
      </div>
      {/* Help Section */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-semibold text-green-900 mb-2">💡 Permission Best Practices</h4>
        <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
          <li>Start with minimal permissions and add more as needed</li>
          <li>Admin level should only be given to trusted roles</li>
          <li>Read access is sufficient for most reporting needs</li>
          <li>Regularly review permissions to maintain security</li>
        </ul>
      </div>
    </div>
  );
}